import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Clock,
  User,
  AlertCircle,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Calendar,
  Tag
} from 'lucide-react';
import { TeamTaskService } from '@/services/team-task.service';
import { Task, TaskStatus, TaskPriority, TaskStatusLabels, TaskPriorityLabels } from '@/models/team-task.model';

export const TasksKanban: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const columns = [
    { status: TaskStatus.draft, label: 'Draft', color: 'bg-gray-100 text-gray-800' },
    { status: TaskStatus.approved, label: 'Approved', color: 'bg-blue-100 text-blue-800' },
    { status: TaskStatus.assigned, label: 'Assigned', color: 'bg-purple-100 text-purple-800' },
    { status: TaskStatus.inProgress, label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
    { status: TaskStatus.blocked, label: 'Blocked', color: 'bg-red-100 text-red-800' },
    { status: TaskStatus.completed, label: 'Completed', color: 'bg-green-100 text-green-800' },
  ];

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const allTasks = await TeamTaskService.getAllTasks();
      setTasks(allTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTasks = (status: TaskStatus) => {
    return tasks.filter(task => {
      const matchesStatus = task.status === status;
      const matchesSearch = searchTerm === '' ||
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;

      return matchesStatus && matchesSearch && matchesPriority;
    });
  };

  const getPriorityBadge = (priority: TaskPriority) => {
    const badges = {
      [TaskPriority.low]: 'bg-gray-100 text-gray-800',
      [TaskPriority.medium]: 'bg-blue-100 text-blue-800',
      [TaskPriority.high]: 'bg-orange-100 text-orange-800',
      [TaskPriority.urgent]: 'bg-red-100 text-red-800',
    };
    return badges[priority];
  };

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      await TeamTaskService.updateTaskStatus(taskId, newStatus);
      await loadTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/team-workspace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tasks Kanban</h1>
            <p className="text-gray-600 mt-1">Manage action items and assignments</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/team-workspace/tasks/new')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Task
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Priority:</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as TaskPriority | 'all')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Priorities</option>
                <option value={TaskPriority.low}>Low</option>
                <option value={TaskPriority.medium}>Medium</option>
                <option value={TaskPriority.high}>High</option>
                <option value={TaskPriority.urgent}>Urgent</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {columns.map((column) => {
          const columnTasks = getFilteredTasks(column.status);
          return (
            <div key={column.status} className="flex-shrink-0 w-80">
              <div className="bg-gray-50 rounded-lg p-4">
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    {column.label}
                    <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs">
                      {columnTasks.length}
                    </span>
                  </h3>
                </div>

                {/* Tasks */}
                <div className="space-y-3">
                  {columnTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No tasks
                    </div>
                  ) : (
                    columnTasks.map((task) => (
                      <div
                        key={task.id}
                        className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/team-workspace/tasks/${task.id}`)}
                      >
                        {/* Task Priority */}
                        <div className="flex items-start justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(task.priority)}`}>
                            {TaskPriorityLabels[task.priority]}
                          </span>
                          {task.containsPHI && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              PHI
                            </span>
                          )}
                        </div>

                        {/* Task Title */}
                        <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                          {task.title}
                        </h4>

                        {/* Task Description */}
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {task.description}
                        </p>

                        {/* Task Meta */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-3">
                            {task.assigneeName && (
                              <div className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {task.assigneeName}
                              </div>
                            )}
                            {task.dueDate && (
                              <div className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </div>
                          {task.subtasks && task.subtasks.length > 0 && (
                            <div className="flex items-center">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {task.subtasks.filter(s => s.isCompleted).length}/{task.subtasks.length}
                            </div>
                          )}
                        </div>

                        {/* Tags */}
                        {task.tags && task.tags.length > 0 && (
                          <div className="flex items-center flex-wrap gap-1 mt-2">
                            {task.tags.slice(0, 3).map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Status Actions */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <select
                            value={task.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, e.target.value as TaskStatus);
                            }}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {Object.values(TaskStatus).map((status) => (
                              <option key={status} value={status}>
                                {TaskStatusLabels[status]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Total Tasks</div>
          <div className="text-2xl font-bold text-gray-900">{tasks.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">In Progress</div>
          <div className="text-2xl font-bold text-yellow-600">
            {tasks.filter(t => t.status === TaskStatus.inProgress).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Completed</div>
          <div className="text-2xl font-bold text-green-600">
            {tasks.filter(t => t.status === TaskStatus.completed).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Blocked</div>
          <div className="text-2xl font-bold text-red-600">
            {tasks.filter(t => t.status === TaskStatus.blocked).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksKanban;
